import {
  FocalLength,
  Image,
  OrientationSystem,
  PixelDimension,
  Position,
  PrincipalPoint,
  Tileset,
  TilesetLevel,
  UndistortedCalibration,
} from '@data-access/geospatial/imaging';
import {
  IMAGES_PAGE_SIZE,
  ImagingApiService,
  WEB3D_COORDINATE_SYSTEM,
} from '@data-access/geospatial/imaging';
import { MobileMappingApiService } from '@data-access/geospatial/mobile-mapping';
import {
  type ScanDataApiService,
  type TcRegion,
} from '@data-access/geospatial/scan-data';
import { PlanarPaneContent } from '@mobile-mapping/web3d';
import { asError, getLogger } from '@trimble/logging';
import { mapSdsStationsToImagingImages } from './sds-station-mapper';

const logger = getLogger();

type StationImage = Image & {
  id: string;
  tileset?: Tileset & {
    uploadUrl: string;
    templateUrl: string;
    tileWidth: number;
    tileHeight: number;
    levels: Array<TilesetLevel>;
  };
  position: Position;
  undistortedCalibration?: UndistortedCalibration & {
    principalPointInPixels: PrincipalPoint & {
      x: number;
      y: number;
    };
    focalLengthInPixels: FocalLength & {
      x: number;
      y: number;
    };
    singlePixelDimensionInMetres: PixelDimension & {
      x: number;
      y: number;
    };
  };
  status: 'Ready' | 'Error';
  captureUtc: string;
};

export interface ValidStations {
  stations: StationImage[];
  name: string;
  isPanorama: boolean;
  id: string;
  runId: string;
  datasetTrn: string;
  orientationSystem?: OrientationSystem;
}

export interface LoadStationImagesOptions {
  readonly scanDataApi?: ScanDataApiService;
  /** When set, used for SDS regional base URL resolution. */
  readonly region?: TcRegion;
  /**
   * Connect regions list; used with `projectLocationFromTenantTrn` when `region`
   * is not yet available (e.g. before `selectedRegion` syncs in the UI).
   */
  readonly regions?: ReadonlyArray<TcRegion>;
  readonly signal?: AbortSignal;
}

/** Connect tenant TRN: `trn:connect:projects:<location>:<projectId>` */
export function projectLocationFromTenantTrn(
  tenantTrn: string | undefined
): string | undefined {
  if (!tenantTrn?.startsWith('trn:connect:projects:')) {
    return undefined;
  }
  const prefix = 'trn:connect:projects:';
  const rest = tenantTrn.slice(prefix.length);
  const colonIdx = rest.indexOf(':');
  if (colonIdx <= 0) {
    return undefined;
  }
  return rest.slice(0, colonIdx);
}

/**
 * Resolve the TC region to use for scan-data calls.
 *
 * Returns `selected` if present; otherwise matches a region from `regionsList`
 * by location, using `locationFromConnect` first and falling back to the
 * location parsed from `tenantTrnSample`.
 */
export function resolveTcRegionForScanData(
  regionsList: ReadonlyArray<TcRegion> | undefined,
  selected: TcRegion | undefined,
  locationFromConnect: string | undefined,
  tenantTrnSample: string | undefined
): TcRegion | undefined {
  if (selected) {
    return selected;
  }
  if (!regionsList?.length) {
    return undefined;
  }
  const loc =
    locationFromConnect ?? projectLocationFromTenantTrn(tenantTrnSample);
  if (!loc) {
    return undefined;
  }
  return regionsList.find((r) => r.location === loc);
}

function resolveSdsTcRegion(
  content: PlanarPaneContent,
  options?: LoadStationImagesOptions
): TcRegion | undefined {
  return resolveTcRegionForScanData(
    options?.regions,
    options?.region,
    undefined,
    content.selectedTenantTrn
  );
}

async function tryLoadSdsStationsAsValidStations(
  content: PlanarPaneContent,
  options?: LoadStationImagesOptions
): Promise<ValidStations[]> {
  const scanDataApi = options?.scanDataApi;
  const region = resolveSdsTcRegion(content, options);

  if (!scanDataApi || !region || !content.selectedPointCloudTrn) {
    return [];
  }

  let stations;
  try {
    stations = await scanDataApi.listAllStationsQuery(
      region,
      content.selectedProjectId,
      content.selectedPointCloudTrn,
      options.signal
    );
  } catch (error: unknown) {
    logger.warn(
      'Scan data: listAllStationsQuery failed; SDS stations will be skipped.',
      asError(error)
    );
    return [];
  }

  if (stations.length === 0) {
    return [];
  }

  let stationTransform;
  try {
    const pointCloudSignal = options.signal ?? new AbortController().signal;
    const pointcloud = await scanDataApi.getPointCloudQuery(
      region,
      content.selectedProjectId,
      content.selectedPointCloudTrn,
      pointCloudSignal
    );
    stationTransform = pointcloud.stationTransform;
  } catch (error: unknown) {
    logger.warn(
      'Scan data: getPointCloudQuery failed; SDS stations still load without point cloud stationTransform.',
      asError(error)
    );
    stationTransform = undefined;
  }

  const panoramaImages = mapSdsStationsToImagingImages(
    stations,
    stationTransform
  );
  const runId = content.selectedRunId;
  const baseId = `sds-stations-${content.selectedPointCloudTrn}`;
  const lists: ValidStations[] = [];

  const toReadyStations = (images: Partial<Image>[]): StationImage[] =>
    images
      .map((image) => ({
        ...image,
        status: 'Ready' as const,
        captureUtc: image.captureUtc ?? '',
      }))
      .sort((a, b) =>
        (a.path ?? '').localeCompare(b.path ?? '')
      ) as StationImage[];

  if (panoramaImages.length > 0) {
    lists.push({
      stations: toReadyStations(panoramaImages),
      name: 'Scan stations (panorama)',
      isPanorama: true,
      id: `${baseId}-panorama`,
      runId,
      datasetTrn: '',
    });
  }

  return lists;
}

export async function loadStationImages(
  content: PlanarPaneContent,
  imagingApi: ImagingApiService,
  mobileMappingApi: MobileMappingApiService,
  options?: LoadStationImagesOptions
): Promise<ValidStations[]> {
  const needsConnectRun = content.scanDataOnly !== true;

  if (
    !content.selectedTenantTrn ||
    !content.selectedProjectId ||
    !content.selectedPointCloudTrn ||
    (needsConnectRun && (!content.selectedMissionTrn || !content.selectedRunId))
  ) {
    return [];
  }

  let validStationsLists: ValidStations[] = [];

  if (needsConnectRun) {
    const camerasResponse = await mobileMappingApi.listCamerasQuery(
      content.selectedTenantTrn,
      content.selectedMissionTrn,
      content.selectedRunId
    );

    const cameras = camerasResponse.items ?? [];

    options?.signal?.throwIfAborted();

    if (cameras.length > 0) {
      // Fetch images for all cameras in parallel
      const imageResults = await Promise.all(
        cameras.map(async (camera) => {
          if (!camera.datasetTrn || !content.selectedTenantTrn) {
            return null;
          }

          try {
            const dataset = await imagingApi.getDatasetQuery(
              content.selectedTenantTrn,
              camera.datasetTrn
            );

            if (!dataset) {
              return null;
            }

            options?.signal?.throwIfAborted();

            const pageSize = IMAGES_PAGE_SIZE;
            const firstPage = await imagingApi.listImagesQuery(
              content.selectedTenantTrn,
              camera.datasetTrn,
              false,
              false,
              WEB3D_COORDINATE_SYSTEM,
              0,
              pageSize
            );

            const allItems = [...(firstPage.items ?? [])];
            const totalItems = firstPage.totalItems ?? 0;
            const totalPages = Math.ceil(totalItems / pageSize);

            options?.signal?.throwIfAborted();

            // Fetch remaining pages if there are more
            if (totalPages > 1) {
              const remainingPagePromises = [];
              for (let pageIndex = 1; pageIndex < totalPages; pageIndex++) {
                remainingPagePromises.push(
                  imagingApi.listImagesQuery(
                    content.selectedTenantTrn,
                    camera.datasetTrn,
                    false,
                    false,
                    WEB3D_COORDINATE_SYSTEM,
                    pageIndex,
                    pageSize
                  )
                );
              }
              const remainingPages = await Promise.all(remainingPagePromises);
              for (const page of remainingPages) {
                allItems.push(...(page.items ?? []));
              }
            }

            return {
              images: { ...firstPage, items: allItems },
              isPanorama: camera.isPanorama,
              name: camera.name,
              datasetTrn: camera.datasetTrn,
              cameraId: camera.id!,
              orientationSystem: dataset.orientationSystem,
            };
          } catch (error: unknown) {
            if (error instanceof Error && error.name === 'AbortError') {
              throw error;
            }
            logger.warn(
              `Failed to load images for camera ${camera.id ?? camera.datasetTrn}; skipping.`,
              asError(error)
            );
            return null;
          }
        })
      );

      const validImageResults = imageResults.filter(
        (result) => result !== null
      );

      if (validImageResults.length > 0) {
        validStationsLists = validImageResults
          .map((img) => {
            if (!img.images.items || img.images.items.length === 0) {
              return undefined;
            }

            return {
              stations: img.images.items
                .filter(
                  (image) =>
                    image.id !== undefined &&
                    image.position !== undefined &&
                    // Panorama images don't have calibration data — only planar cameras need it
                    (img.isPanorama ||
                      image.undistortedCalibration !== undefined)
                )
                .map((image) => ({
                  ...image,
                  status: 'Ready' as const,
                }))
                // Sorting shouldn't be needed, but helps in case when capture data repeats due to a backend problem.
                .sort((a, b) =>
                  (a.path ?? '').localeCompare(b.path ?? '')
                ) as StationImage[],
              name: img.name || img.datasetTrn,
              isPanorama: img.isPanorama || false,
              id: img.cameraId,
              runId: content.selectedRunId,
              datasetTrn: img.datasetTrn,
              orientationSystem: img.orientationSystem,
            };
          })
          .filter((x) => x !== undefined);
      }
    }
  }

  const sdsStationsLists = await tryLoadSdsStationsAsValidStations(
    content,
    options
  );

  return [...validStationsLists, ...sdsStationsLists];
}
